import { useLocation } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';
import { PRICING_CONFIG } from '../config/pricingConfig';

/**
 * TrialExpiredOverlay
 *
 * Full-screen overlay shown when the warehouse's 30-day trial has expired.
 * Blocks interaction but still allows the user to contact support to upgrade or log out.
 * Verified platform admins (user.isAdmin === true) are not blocked on /admin routes.
 */
export function TrialExpiredOverlay() {
    const { user, logout } = useAuthStore();
    const [location] = useLocation();

    if (
        !user ||
        user.role === 'carrier' ||
        user.trialStatus !== 'expired' ||
        (user.isAdmin === true && location.startsWith('/admin'))
    ) {
        return null;
    }

    const endDate = user.trialEndAt
        ? new Date(user.trialEndAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : 'recently';

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(15, 23, 42, 0.92)',
            backdropFilter: 'blur(8px)',
        }}>
            <div style={{
                maxWidth: '480px',
                width: '90%',
                textAlign: 'center',
                padding: '44px 32px',
                borderRadius: '20px',
                background: 'linear-gradient(145deg, rgba(30,41,59,0.95), rgba(15,23,42,0.98))',
                border: '1px solid rgba(20,184,166,0.35)',
                boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
            }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏱️</div>
                <h2 style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#f1f5f9',
                    marginBottom: '12px',
                }}>
                    Free Trial Ended
                </h2>
                <p style={{
                    fontSize: '15px',
                    color: '#94a3b8',
                    lineHeight: 1.6,
                    marginBottom: '8px',
                }}>
                    Your 30-day free trial ended on {endDate}.
                </p>
                <p style={{
                    fontSize: '14px',
                    color: '#cbd5e1',
                    lineHeight: 1.6,
                    marginBottom: '28px',
                }}>
                    Upgrade to <strong>Starter (${PRICING_CONFIG.plans.starter.priceMonthlyUsd} USD/month per facility)</strong> or <strong>Operations (${PRICING_CONFIG.plans.operations.priceMonthlyUsd} USD/month per facility)</strong> to continue using BayWhiz.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                    <button
                        onClick={() => window.location.href = `mailto:${PRICING_CONFIG.supportEmail}?subject=BayWhiz%20Plan%20Upgrade`}
                        style={{
                            padding: '14px 32px',
                            borderRadius: '12px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #0d9488, #0f766e)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '15px',
                            cursor: 'pointer',
                            width: '100%',
                            maxWidth: '280px',
                            transition: 'transform 0.15s',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                        onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                        Contact Us to Upgrade
                    </button>
                    <button
                        onClick={async () => {
                            const ok = await logout();
                            if (ok) {
                                window.location.replace('/login');
                            }
                        }}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '10px',
                            border: '1px solid rgba(148,163,184,0.3)',
                            background: 'transparent',
                            color: '#94a3b8',
                            fontSize: '13px',
                            cursor: 'pointer',
                            width: '100%',
                            maxWidth: '280px',
                        }}
                    >
                        Log Out
                    </button>
                </div>
            </div>
        </div>
    );
}
